import React from 'react';
import lodash from 'lodash';
import classnames from 'classnames';

import { getEmpHistoryDate, getFormattedDate } from 'utils/date';
import { mergeDesignationAndEngagementStatus } from 'utils/user';

import Divider from 'components/common/divider/Divider';

import { HIRED } from 'constants/employeeStatus';
import { DATE_FORMAT_FOR_DISPLAY } from 'constants/appConstants';

import { LeapfrogHistory } from 'interface/createUpdateForm';
import { EmployeeLike } from 'interface/userUtils';

interface EmpStatusDetail {
  id: number;
  category: string;
  date: string;
}

interface EmploymentHistoryProps {
  empStatusHistory?: LeapfrogHistory['empStatusHistory'];
  designationAreaHistory?: LeapfrogHistory['designationAreaHistory'];
}

const EmploymentHistory: React.FC<EmploymentHistoryProps> = (props) => {
  const { empStatusHistory = [], designationAreaHistory = [] } = props;

  const sortedEmpStatusHistory = lodash.sortBy(empStatusHistory, 'transitionDate');
  const sortedDesignationAreaHistory = lodash.sortBy(designationAreaHistory, 'transitionDate');

  const mergedData = mergeDesignationAndEngagementStatus(
    sortedEmpStatusHistory,
    sortedDesignationAreaHistory,
    'transitionDate'
  );

  const descendingMergedData = lodash.orderBy(mergedData, 'transitionDate', 'desc');

  let currentDesignationIndex = 0;

  return descendingMergedData.map((rawData, index, arr) => {
    const data = rawData as EmployeeLike & {
      endDate?: string;
      empStatusDetails?: EmpStatusDetail[];
      designation?: { name?: string };
      area?: { name?: string };
    };
    const transitionDate = typeof data.transitionDate === 'string' ? data.transitionDate : '';
    const engagementStatusName = data.engagementStatus?.name || '';
    const designationName = data.designation?.name || '';
    const areaName = data.area?.name || '';
    const isActive = index === currentDesignationIndex;
    const isHired = engagementStatusName === HIRED;
    const details = Array.isArray(data.empStatusDetails) ? data.empStatusDetails : [];

    if (getFormattedDate() < transitionDate) {
      currentDesignationIndex++;
    }

    const areaSuffix = areaName ? `, ${areaName}` : '';
    const name = isHired ? HIRED : `${designationName}${areaSuffix}`;

    return (
      <div className="position-relative" key={`${transitionDate}-${index}`}>
        <div
          className={classnames('employee-status-wrapper', {
            'employee-status-wrapper-last': index === mergedData.length - 1,
            'remove-border': index !== mergedData.length - 1 && arr[index + 1].engagementStatus.name === 'Terminated',
          })}
        >
          <div className="employee-status">
            <div
              className={classnames('designation-title font-bold', {
                'current-designation-title': isActive,
              })}
            >
              {name}
            </div>

            <div className="emp-history">{getEmpHistoryDate(transitionDate, data.endDate)}</div>
            {!isHired && <div className="engagement-status">{engagementStatusName}</div>}
            <div className="engagement-details">
              {!!details.length &&
                [...lodash.sortBy(details, 'date')].reverse().map((item, detailIndex) => {
                  return (
                    <p key={`${item.id}-${detailIndex}`}>
                      <span className="engagement-details__category">{`${item.category} : `}</span>
                      <span className="engagement-details__date">{`${getFormattedDate(
                        typeof item.date === 'string' ? item.date : '',
                        DATE_FORMAT_FOR_DISPLAY
                      )}`}</span>
                    </p>
                  );
                })}
            </div>
          </div>

          {index !== mergedData.length - 1 && arr[index + 1].engagementStatus.name === 'Terminated' && (
            <Divider customClassName="employee-status-spaced-border" />
          )}
        </div>
      </div>
    );
  });
};

export default EmploymentHistory;
